import React, { useMemo } from "react";
import { AlertCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  useProcessingIndicator,
  type ProcessingIndicator,
} from "@/components/application/ProcessingToast";
import type { BorrowerDetail } from "@/types/borrower";
import {
  PLAID_PROCESSING_BANNER_STATUSES,
  isPlaidAnalysisBehindReport,
  latestAnalysis,
  latestDecision,
} from "./finalUnderwritingDisplay";

const PROCESSING_TITLES = {
  processing_report: "Plaid asset report is being generated",
  processing_analysis: "Plaid asset report received — running analysis",
} as const;

const PLAID_PROCESSING_DESCRIPTION =
  "This usually takes a few minutes. You can leave this page; refresh to see results.";

export type PlaidStatusMode = "processing_report" | "processing_analysis" | "error" | null;

interface PlaidStatusBannerProps {
  borrower: BorrowerDetail;
  /** When true (analysis already rendered below), hide idle processing for early statuses. */
  hasAnalysis?: boolean;
}

/**
 * STE-361 hide-when-complete lives here. In-flight modes feed the page toast;
 * failure stays in the tab as a card. Before the merchant submits accounts,
 * MerchantChecklist owns the message and this banner stays out of it.
 */
export function plaidStatusMode(borrower: BorrowerDetail, hasAnalysis?: boolean): PlaidStatusMode {
  const reportStatus = borrower.plaid_report_status;
  const decision = latestDecision(borrower, "plaid");
  const analysisOwedForReport = isPlaidAnalysisBehindReport(borrower);

  // Explicit failure always wins (including funded/final UW refreshes).
  if (reportStatus === "failed") return "error";

  // New / in-flight asset report (any app status, including funded).
  if (reportStatus === "processing") return "processing_report";

  // AI analysis / underwriting running for the Plaid lane.
  if (decision?.decision_status === "processing") return "processing_analysis";

  // Report ready but underwriting for this report has not landed yet.
  if (reportStatus === "ready" && analysisOwedForReport) return "processing_analysis";

  if (!PLAID_PROCESSING_BANNER_STATUSES.includes(borrower.app_status)) return null;

  if (borrower.app_status === "contract_signed") {
    return null;
  }

  if (borrower.app_status === "plaid_initiated") {
    // null or "pending": merchant hasn't submitted their accounts yet.
    if (reportStatus == null || reportStatus === "pending") return null;
    return hasAnalysis ? null : "processing_analysis";
  }

  // plaid_completed: analysis is running unless it's already on screen.
  return hasAnalysis ? null : "processing_analysis";
}

/**
 * Title-only by design: no startedAt and no elapsedSeconds. The client is never
 * given a trustworthy Plaid start time, and timing from mount would report the
 * page's age rather than the report's. Do not invent one to match the ticking
 * statement lane.
 */
export function plaidProcessingIndicator(
  borrower: BorrowerDetail,
  hasAnalysis?: boolean
): ProcessingIndicator | null {
  const mode = plaidStatusMode(borrower, hasAnalysis);
  if (mode !== "processing_report" && mode !== "processing_analysis") return null;
  return {
    title: PROCESSING_TITLES[mode],
    description: PLAID_PROCESSING_DESCRIPTION,
  };
}

/**
 * Registers Plaid in-flight work on the page toast. Lives next to the toast,
 * not inside the lazy Final Underwriting tab, so the lane stays visible on
 * every other tab.
 */
export const PlaidProcessingPublisher: React.FC<{
  borrower: BorrowerDetail;
  hasAnalysis?: boolean;
}> = ({ borrower, hasAnalysis }) => {
  const analysisShown = hasAnalysis ?? Boolean(latestAnalysis(borrower, "plaid"));
  const indicator = useMemo(
    () => plaidProcessingIndicator(borrower, analysisShown),
    [borrower, analysisShown]
  );
  useProcessingIndicator(indicator);
  return null;
};

const PlaidStatusBanner: React.FC<PlaidStatusBannerProps> = ({ borrower, hasAnalysis }) => {
  const mode = useMemo(() => plaidStatusMode(borrower, hasAnalysis), [borrower, hasAnalysis]);

  if (mode === "error") {
    const err = borrower.plaid_report_error;
    return (
      <Card className="border-destructive/30 bg-destructive/5 p-6 shadow-none">
        <div className="mb-3 flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div>
            <h2 className="text-lg font-semibold">Plaid asset report failed</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {err?.message ||
                "Plaid reported an error generating the asset report. Use Retry asset report fetch to create a new report from connected banks."}
              {err?.code ? ` (code: ${err.code})` : null}
            </p>
          </div>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div className={cn("h-full w-1/3 rounded-full bg-destructive")} />
        </div>
      </Card>
    );
  }

  return null;
};

export default PlaidStatusBanner;
